import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, CardActions,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Snackbar, Divider, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Group, PersonSearch } from '@mui/icons-material';
import {
  fetchSegments, createSegment, updateSegment, deleteSegment,
  computeSegment, fetchSegmentMembers,
} from '../../api/api';

const FIELDS = [
  { value: 'totalSpent', label: 'Total Spent (KES)', type: 'number' },
  { value: 'orderCount', label: 'Order Count', type: 'number' },
  { value: 'daysSinceLastOrder', label: 'Days Since Last Order', type: 'number' },
  { value: 'hasOrdered', label: 'Has Ordered', type: 'boolean' },
  { value: 'categoryPreference', label: 'Category Preference', type: 'string' },
];

const OPERATORS_BY_TYPE = {
  number: ['is greater than', 'is less than', 'equals', 'is not'],
  boolean: ['equals'],
  string: ['equals', 'is not'],
};

const EMPTY_RULE = { field: 'totalSpent', operator: 'is greater than', value: '' };
const EMPTY_FORM = { name: '', description: '', rules: [{ ...EMPTY_RULE }] };

export default function CustomerSegments() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSegment, setEditSegment] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [membersDialog, setMembersDialog] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersPage, setMembersPage] = useState(0);
  const [membersTotal, setMembersTotal] = useState(0);
  const [computing, setComputing] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchSegments();
      setSegments(res.data?.segments || res.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const openAdd = () => {
    setEditSegment(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (seg) => {
    setEditSegment(seg);
    setForm({
      name: seg.name || '',
      description: seg.description || '',
      rules: seg.rules?.length ? seg.rules.map(r => ({ field: r.field, operator: r.operator, value: r.value })) : [{ ...EMPTY_RULE }],
    });
    setDialogOpen(true);
  };

  const addRule = () => setForm(f => ({ ...f, rules: [...f.rules, { ...EMPTY_RULE }] }));
  const removeRule = (i) => setForm(f => ({ ...f, rules: f.rules.filter((_, idx) => idx !== i) }));
  const updateRule = (i, key, val) => setForm(f => ({
    ...f,
    rules: f.rules.map((r, idx) => idx === i ? { ...r, [key]: val, ...(key === 'field' ? { operator: OPERATORS_BY_TYPE[FIELDS.find(f => f.value === val)?.type || 'number']?.[0] || 'equals', value: '' } : {}) } : r),
  }));

  const handleSubmit = async () => {
    if (!form.name.trim()) return showSnack('Name is required', 'error');
    setSaving(true);
    try {
      if (editSegment) {
        await updateSegment(editSegment._id, form);
        showSnack('Segment updated');
      } else {
        await createSegment(form);
        showSnack('Segment created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      showSnack(e.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSegment(id);
      setDeleteConfirm(null);
      showSnack('Segment deleted');
      load();
    } catch (e) {
      showSnack('Delete failed', 'error');
    }
  };

  const handleCompute = async (seg) => {
    setComputing(c => ({ ...c, [seg._id]: true }));
    try {
      const res = await computeSegment(seg._id);
      showSnack(`Recomputed: ${res.data?.memberCount || 0} members`);
      load();
    } catch (e) {
      showSnack('Compute failed', 'error');
    } finally {
      setComputing(c => ({ ...c, [seg._id]: false }));
    }
  };

  const openMembers = async (seg, page = 0) => {
    setMembersDialog(seg);
    setMembersLoading(true);
    try {
      const res = await fetchSegmentMembers(seg._id, { page: page + 1, limit: 10 });
      setMembers(res.data?.members || res.data?.users || []);
      setMembersTotal(res.data?.total || 0);
      setMembersPage(page);
    } catch {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Customer Segments</Typography>
          <Typography variant="body2" color="text.secondary">Create rule-based customer groups for targeted marketing</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
          Create Segment
        </Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && segments.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Group sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No segments yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Create your first customer segment</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            Create Segment
          </Button>
        </Paper>
      )}

      <Grid container spacing={2}>
        {segments.map(seg => (
          <Grid item xs={12} sm={6} md={4} key={seg._id}>
            <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                  <Typography fontWeight={800} color="text.primary">{seg.name}</Typography>
                  <Chip label={`${seg.memberCount || 0} members`} size="small"
                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 700 }} />
                </Box>
                {seg.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{seg.description}</Typography>}
                {seg.rules?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {seg.rules.slice(0, 3).map((r, i) => (
                      <Chip key={i} label={`${r.field} ${r.operator} ${r.value}`} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                    ))}
                    {seg.rules.length > 3 && <Chip label={`+${seg.rules.length - 3} more`} size="small" variant="outlined" sx={{ fontSize: 10 }} />}
                  </Box>
                )}
                {seg.lastComputed && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Last computed: {new Date(seg.lastComputed).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, gap: 0.5, flexWrap: 'wrap' }}>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(seg)} color="primary"><Edit fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Recompute">
                  <IconButton size="small" onClick={() => handleCompute(seg)} disabled={computing[seg._id]} color="info">
                    {computing[seg._id] ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="View Members">
                  <IconButton size="small" onClick={() => openMembers(seg)} color="success"><PersonSearch fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setDeleteConfirm(seg)} color="error"><Delete fontSize="small" /></IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={800}>{editSegment ? 'Edit Segment' : 'Create Segment'}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight={700}>Rules</Typography>
              <Button size="small" startIcon={<Add />} onClick={addRule}>Add Rule</Button>
            </Box>
            {form.rules.map((rule, i) => {
              const fieldDef = FIELDS.find(f => f.value === rule.field);
              const ops = OPERATORS_BY_TYPE[fieldDef?.type || 'number'] || ['equals'];
              return (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                    <InputLabel>Field</InputLabel>
                    <Select value={rule.field} label="Field" onChange={e => updateRule(i, 'field', e.target.value)}>
                      {FIELDS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select value={rule.operator} label="Operator" onChange={e => updateRule(i, 'operator', e.target.value)}>
                      {ops.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Value" value={rule.value}
                    onChange={e => updateRule(i, 'value', e.target.value)}
                    type={fieldDef?.type === 'number' ? 'number' : 'text'}
                    sx={{ flex: 1, minWidth: 80 }} />
                  <IconButton size="small" onClick={() => removeRule(i)} color="error" disabled={form.rules.length === 1}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : (editSegment ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={Boolean(membersDialog)} onClose={() => setMembersDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={800}>Members: {membersDialog?.name}</DialogTitle>
        <DialogContent>
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell>Last Order</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary">No members</Typography></TableCell></TableRow>
                    ) : (
                      members.map((m, i) => (
                        <TableRow key={i}>
                          <TableCell>{m.name || m.displayName}</TableCell>
                          <TableCell>{m.email}</TableCell>
                          <TableCell>{m.phone}</TableCell>
                          <TableCell align="right">{m.orderCount || m.orders || 0}</TableCell>
                          <TableCell>{m.lastOrder ? new Date(m.lastOrder).toLocaleDateString() : '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={membersTotal}
                page={membersPage}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
                onPageChange={(_, p) => openMembers(membersDialog, p)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setMembersDialog(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={800}>Delete Segment</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?._id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
